import { useEffect, useRef, useState } from "react";
import { Plus, Trash, PencilSimple, Package, Wrench, Cube, FilePdf, UploadSimple, FileX, FileText } from "@phosphor-icons/react";
import { toast } from "sonner";
import Layout from "../components/Layout";
import api, { API } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { OwnerPill } from "../lib/usersMap";

const TYPES = ["product", "service", "material"];
const TYPE_ICON = { product: Cube, service: Wrench, material: Package };
const empty = { name: "", category: "General", unit: "Unit", price: 0, description: "", type: "product", core_features: "", tone_style: "professional healthcare marketing" };

const STATUS_STYLE = {
  draft: { label: "Draft", cls: "bg-[#F5F6F8] text-[#717680] border-black/10" },
  active: { label: "Active", cls: "bg-[#0D9488]/10 text-[#0D9488] border-[#0D9488]/30" },
  generate_social_assets: { label: "Queued", cls: "bg-[#F4A261]/15 text-[#F4A261] border-[#F4A261]/30" },
  generating: { label: "Generating…", cls: "bg-[#F4A261]/15 text-[#F4A261] border-[#F4A261]/30 animate-pulse" },
  ready_for_review: { label: "Review", cls: "bg-[#D90429]/10 text-[#D90429] border-[#D90429]/30" },
  archived: { label: "Archived", cls: "bg-black/5 text-[#717680] border-black/10" },
};

export default function Catalog() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(30);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("all");
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [uploadingId, setUploadingId] = useState(null);
  const fileRefs = useRef({});
  const bulkFileRef = useRef(null);
  const [bulkUploading, setBulkUploading] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);
  const [webhookOpen, setWebhookOpen] = useState(false);
  const [webhookCfg, setWebhookCfg] = useState({ webhook_url: "", api_key: "", has_config: false });
  const [webhookSaving, setWebhookSaving] = useState(false);
  const [webhookTesting, setWebhookTesting] = useState(false);
  const [generatingId, setGeneratingId] = useState(null);

  const loadWebhookCfg = async () => {
    try {
      const { data } = await api.get("/products/webhook/settings");
      setWebhookCfg(data);
    } catch { /* ignore */ }
  };

  const saveWebhookCfg = async () => {
    setWebhookSaving(true);
    try {
      const { data } = await api.put("/products/webhook/settings", { webhook_url: webhookCfg.webhook_url });
      setWebhookCfg(data);
      toast.success("Webhook settings saved. API key generated — copy it into Emergent AI.");
    } catch (err) { toast.error(err?.response?.data?.detail || "Save failed"); }
    finally { setWebhookSaving(false); }
  };

  const testWebhookCfg = async () => {
    setWebhookTesting(true);
    try {
      const { data } = await api.post("/products/webhook/test");
      if (data.ok) toast.success(`Ping OK · HTTP ${data.status_code}`);
      else toast.error(`Ping failed · HTTP ${data.status_code} · ${(data.response || "").slice(0, 120)}`);
    } catch (err) { toast.error(err?.response?.data?.detail || "Test failed"); }
    finally { setWebhookTesting(false); }
  };

  const triggerGenerateAssets = async (it) => {
    setGeneratingId(it.id);
    try {
      await api.put(`/products/${it.id}`, { ...it, status: "generate_social_assets" });
      if (webhookCfg.has_config) {
        toast.success(`Webhook fired for ${it.name}. Waiting for external service to POST back the asset.`);
      } else {
        toast.success(`Generating ${it.name} in-house via Nano Banana… image will appear in ~15 s. Refresh to see progress.`);
      }
      load();
      // Auto-refresh in 20s so user sees the ready_for_review state without manual refresh
      setTimeout(() => load(), 20000);
    } catch (err) { toast.error(err?.response?.data?.detail || "Trigger failed"); }
    finally { setGeneratingId(null); }
  };

  const approveAsset = async (id) => {
    try {
      const { data } = await api.post(`/products/${id}/approve`);
      if (data.linkedin_posted) toast.success("Approved · Posted to LinkedIn ✓");
      else if (data.linkedin_error) toast.success(`Approved · LinkedIn skipped (${data.linkedin_error.slice(0, 80)})`);
      else toast.success("Approved · LinkedIn not connected — image saved on product");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Approve failed"); }
  };

  const load = async (opts = {}) => {
    setLoading(true);
    try {
      const p = opts.page ?? page;
      const s = opts.search ?? search;
      const c = opts.category ?? category;
      const { data } = await api.get("/products", {
        params: { paginated: 1, page: p, limit, search: s, category: c },
      });
      setItems(data.items || []);
      setTotal(data.total || 0);
    } finally {
      setLoading(false);
    }
  };

  const loadCategories = async () => {
    try {
      const { data } = await api.get("/products/categories");
      setCategories(data || []);
    } catch { /* ignore */ }
  };

  // Initial load
  useEffect(() => { load({ page: 1 }); loadCategories(); loadWebhookCfg(); /* eslint-disable-next-line */ }, []);

  // Debounced search/category change
  useEffect(() => {
    const t = setTimeout(() => {
      setPage(1);
      load({ page: 1, search, category });
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [search, category]);

  const save = async () => {
    if (!form.name.trim()) { toast.error("Name required"); return; }
    const payload = { ...form, price: parseFloat(form.price || 0) };
    if (editing) await api.put(`/products/${editing.id}`, payload);
    else await api.post("/products", payload);
    toast.success(editing ? "Updated" : "Added");
    setOpen(false);
    load();
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this item?")) return;
    await api.delete(`/products/${id}`);
    setItems((curr) => curr.filter((it) => it.id !== id));
    setTotal((t) => Math.max(0, t - 1));
    toast.success("Deleted");
  };

  const triggerBrochureUpload = (id) => {
    if (fileRefs.current[id]) fileRefs.current[id].click();
  };

  const handleBulkUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    setBulkUploading(true);
    setBulkResult(null);
    try {
      const { data } = await api.post("/products/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setBulkResult(data);
      if (data.inserted > 0) {
        toast.success(
          `Imported ${data.inserted} item${data.inserted > 1 ? "s" : ""}` +
            (data.skipped_duplicates ? ` · ${data.skipped_duplicates} duplicate${data.skipped_duplicates > 1 ? "s" : ""} skipped` : "")
        );
      } else if (data.skipped_duplicates > 0) {
        toast.message(`All ${data.skipped_duplicates} rows already exist — nothing imported`);
      } else {
        toast.warning("No rows imported. Check that the sheet has a 'Name' column.");
      }
      setPage(1);
      load({ page: 1 });
      loadCategories();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setBulkUploading(false);
      e.target.value = "";
    }
  };

  const handleBrochureChange = async (e, id) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 25 * 1024 * 1024) { toast.error("File too large (max 25 MB)"); e.target.value = ""; return; }
    const fd = new FormData();
    fd.append("file", f);
    setUploadingId(id);
    try {
      await api.post(`/products/${id}/brochure`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Brochure attached");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploadingId(null);
      e.target.value = "";
    }
  };

  const removeBrochure = async (id) => {
    if (!window.confirm("Remove brochure from this item?")) return;
    await api.delete(`/products/${id}/brochure`);
    toast.success("Brochure removed");
    load();
  };

  return (
    <Layout
      title="Catalog"
      subtitle={`${total.toLocaleString("en-IN")} item${total === 1 ? "" : "s"} · Products, services & materials`}
      actions={
        <>
          <input
            ref={bulkFileRef}
            type="file"
            accept=".xlsx,.xls,.csv"
            onChange={handleBulkUpload}
            className="hidden"
            data-testid="catalog-bulk-upload-input"
          />
          <button onClick={() => setWebhookOpen(true)} data-testid="webhook-settings-button" className="hidden md:inline-flex items-center gap-2 px-4 py-2.5 border border-black/15 text-sm font-semibold hover:bg-[#0A0A0A] hover:text-white transition-all">
            🎨 Asset Webhook {webhookCfg.has_config ? <span className="text-[10px] text-[#0D9488] font-mono-shelv">● EXTERNAL</span> : <span className="text-[10px] text-[#0D9488] font-mono-shelv">● IN-HOUSE</span>}
          </button>
          <button onClick={() => bulkFileRef.current?.click()} disabled={bulkUploading} data-testid="bulk-upload-products-button" className="hidden md:inline-flex items-center gap-2 px-4 py-2.5 border border-black/15 text-sm font-semibold hover:bg-[#0A0A0A] hover:text-white transition-all disabled:opacity-50">
            <UploadSimple size={16} weight="bold" /> {bulkUploading ? "Importing…" : "Import Excel"}
          </button>
          <button onClick={() => bulkFileRef.current?.click()} disabled={bulkUploading} aria-label="Import Excel" data-testid="bulk-upload-products-mobile" className="md:hidden p-2 border border-black/15 disabled:opacity-50">
            <UploadSimple size={16} weight="bold" />
          </button>
          <button onClick={() => { setEditing(null); setForm(empty); setOpen(true); }} data-testid="add-product-button" className="inline-flex items-center gap-1.5 md:gap-2 px-3 md:px-4 py-2 md:py-2.5 bg-[#D90429] text-white text-xs md:text-sm font-semibold hover:bg-[#EF233C] transition-all active:scale-95">
            <Plus size={16} weight="bold" /> <span className="hidden sm:inline">Add Item</span><span className="sm:hidden">Add</span>
          </button>
        </>
      }
    >
      {bulkResult && (
        <div className="mb-5 border-l-4 border-[#D90429] bg-[#FFE5E9]/40 px-4 py-3 text-sm flex items-start justify-between gap-4" data-testid="catalog-bulk-result">
          <div>
            <div className="font-bold text-[#0A0A0A]">
              Import complete · {bulkResult.inserted} item{bulkResult.inserted === 1 ? "" : "s"} added
            </div>
            <div className="text-xs text-[#4A4D57] mt-1">
              {bulkResult.skipped_duplicates > 0 && <span>{bulkResult.skipped_duplicates} duplicate{bulkResult.skipped_duplicates === 1 ? "" : "s"} skipped · </span>}
              {bulkResult.skipped_blank > 0 && <span>{bulkResult.skipped_blank} blank row{bulkResult.skipped_blank === 1 ? "" : "s"} skipped · </span>}
              {Object.entries(bulkResult.per_sheet || {}).map(([n, v]) => `${n}: ${v}`).join(" · ") || "No per-sheet detail"}
              {bulkResult.unmatched_sheets?.length > 0 && (
                <span className="block mt-0.5 text-[#D90429]">Skipped sheets (no &lsquo;Name&rsquo; column): {bulkResult.unmatched_sheets.join(", ")}</span>
              )}
            </div>
          </div>
          <button onClick={() => setBulkResult(null)} className="text-xs text-[#717680] hover:text-[#0A0A0A]" data-testid="dismiss-catalog-import">Dismiss</button>
        </div>
      )}

      {/* Search & filter bar */}
      <div className="flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-center gap-2 md:gap-3 mb-5">
        <Input
          placeholder="Search products by name, category, SKU…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full sm:max-w-sm rounded-none"
          data-testid="catalog-search-input"
        />
        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger className="w-full sm:w-48 rounded-none" data-testid="catalog-category-filter"><SelectValue placeholder="All categories" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All categories</SelectItem>
            {categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
        {loading && <span className="text-xs font-mono-shelv text-[#717680] tracking-widest" data-testid="catalog-loading">Loading…</span>}
      </div>

      {items.length === 0 ? (
        <div className="border border-black/10 p-16 text-center text-[#717680]" data-testid="empty-catalog">
          <Package size={36} className="mx-auto text-[#D90429]" />
          <p className="mt-4">Your catalog is empty. Add products, services or materials to use them in proposals.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="catalog-grid">
          {items.map((it) => {
            const Icon = TYPE_ICON[it.type] || Cube;
            const hasBrochure = !!it.brochure_path;
            const uploading = uploadingId === it.id;
            return (
              <div key={it.id} className="border border-black/10 bg-white p-5 hover:-translate-y-0.5 hover:shadow-sm transition-all flex flex-col" data-testid={`product-card-${it.id}`}>
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Icon size={20} weight="bold" className="text-[#D90429]" />
                    <span className="text-xs uppercase tracking-widest font-bold text-[#717680]">{it.type}</span>
                    <OwnerPill userId={it.user_id} />
                    {(() => { const s = STATUS_STYLE[it.status || "active"] || STATUS_STYLE.active; return (
                      <span className={`text-[10px] font-mono-shelv uppercase tracking-widest border px-1.5 py-0.5 ${s.cls}`} data-testid={`product-status-${it.id}`}>{s.label}</span>
                    );})()}
                  </div>
                  <div className="flex gap-1">
                    <button onClick={() => { setEditing(it); setForm({ ...empty, ...it }); setOpen(true); }} className="p-1.5 hover:bg-black/5" data-testid={`edit-product-${it.id}`}><PencilSimple size={14} /></button>
                    <button onClick={() => remove(it.id)} className="p-1.5 hover:bg-[#D90429]/10 text-[#D90429]" data-testid={`delete-product-${it.id}`}><Trash size={14} /></button>
                  </div>
                </div>
                <h3 className="font-display text-xl font-bold tracking-tight mt-3">{it.name}</h3>
                <p className="text-xs text-[#717680] mt-1">{it.category}</p>
                {it.description && <p className="text-sm text-[#4A4D57] mt-3 line-clamp-2">{it.description}</p>}

                {/* Generated asset preview + approve */}
                {it.generated_asset_url && (
                  <div className="mt-4 border border-[#D90429]/20 bg-[#FFE5E9]/20 p-2" data-testid={`asset-preview-${it.id}`}>
                    <img src={it.generated_asset_url} alt="Generated" className="w-full h-32 object-cover" data-testid={`asset-image-${it.id}`}/>
                    {it.status === "ready_for_review" && (
                      <button onClick={() => approveAsset(it.id)} className="mt-2 w-full py-1.5 bg-[#D90429] text-white text-[11px] font-mono-shelv uppercase tracking-widest hover:bg-[#EF233C]" data-testid={`approve-asset-${it.id}`}>
                        ✓ Approve &amp; auto-post to LinkedIn
                      </button>
                    )}
                  </div>
                )}

                {/* Generate social assets trigger */}
                {["active", "draft"].includes(it.status || "active") && !it.generated_asset_url && (
                  <button
                    onClick={() => triggerGenerateAssets(it)}
                    disabled={generatingId === it.id}
                    className="mt-3 w-full py-2 border border-dashed border-[#D90429]/40 hover:bg-[#D90429]/5 text-[11px] font-mono-shelv uppercase tracking-widest text-[#D90429] disabled:opacity-50"
                    data-testid={`generate-assets-${it.id}`}
                  >
                    {generatingId === it.id ? "Starting…" : "▶ Generate social assets"}
                  </button>
                )}

                {/* Brochure section */}
                <input
                  type="file"
                  accept="application/pdf,image/png,image/jpeg,image/webp"
                  ref={(el) => (fileRefs.current[it.id] = el)}
                  onChange={(e) => handleBrochureChange(e, it.id)}
                  className="hidden"
                  data-testid={`brochure-input-${it.id}`}
                />
                <div className="mt-4">
                  {hasBrochure ? (
                    <div className="flex items-center gap-2 border border-[#D90429]/30 bg-[#D90429]/5 px-3 py-2" data-testid={`brochure-attached-${it.id}`}>
                      <FilePdf size={18} weight="bold" className="text-[#D90429] shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-semibold truncate">{it.brochure_filename || "Brochure"}</div>
                        <div className="flex items-center gap-3 mt-0.5">
                          <a
                            href={`${API}/products/${it.id}/brochure`}
                            target="_blank"
                            rel="noreferrer"
                            className="text-[10px] font-mono-shelv uppercase tracking-widest text-[#D90429] hover:underline"
                            data-testid={`view-brochure-${it.id}`}
                          >
                            View
                          </a>
                          <button
                            onClick={() => triggerBrochureUpload(it.id)}
                            className="text-[10px] font-mono-shelv uppercase tracking-widest text-[#717680] hover:text-[#0A0A0A]"
                            data-testid={`replace-brochure-${it.id}`}
                          >
                            Replace
                          </button>
                          <button
                            onClick={() => removeBrochure(it.id)}
                            className="text-[10px] font-mono-shelv uppercase tracking-widest text-[#717680] hover:text-[#D90429]"
                            data-testid={`remove-brochure-${it.id}`}
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <button
                      onClick={() => triggerBrochureUpload(it.id)}
                      disabled={uploading}
                      className="w-full inline-flex items-center justify-center gap-2 border border-dashed border-black/20 hover:border-[#D90429] hover:bg-[#D90429]/5 text-sm text-[#4A4D57] hover:text-[#D90429] py-2.5 transition-all disabled:opacity-50"
                      data-testid={`upload-brochure-${it.id}`}
                    >
                      {uploading ? <><UploadSimple size={16} weight="bold" className="animate-pulse" /> Uploading…</> : <><UploadSimple size={16} weight="bold" /> Attach brochure</>}
                    </button>
                  )}
                </div>

                <div className="mt-4 pt-4 border-t border-black/5 flex items-end justify-between">
                  <div>
                    <div className="overline text-[10px]">Price</div>
                    <div className="font-display text-2xl font-black tracking-tighter">₹{Number(it.price).toLocaleString("en-IN")}</div>
                  </div>
                  <div className="text-xs text-[#717680]">per {it.unit}</div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Pagination footer */}
      {total > limit && (
        <div className="mt-5 flex flex-col sm:flex-row items-center justify-between gap-3 border-t border-black/10 pt-4" data-testid="catalog-pagination">
          <div className="text-xs font-mono-shelv text-[#717680] tracking-widest uppercase">
            Page {page} of {Math.max(1, Math.ceil(total / limit))} · {total.toLocaleString("en-IN")} total
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => { const p = Math.max(1, page - 1); setPage(p); load({ page: p }); window.scrollTo({ top: 0, behavior: "smooth" }); }}
              disabled={page <= 1 || loading}
              className="px-3 py-2 border border-black/15 text-xs font-semibold hover:bg-black/5 disabled:opacity-40"
              data-testid="catalog-prev-page"
            >
              ← Prev
            </button>
            <button
              onClick={() => { const p = page + 1; setPage(p); load({ page: p }); window.scrollTo({ top: 0, behavior: "smooth" }); }}
              disabled={page * limit >= total || loading}
              className="px-3 py-2 border border-black/15 text-xs font-semibold hover:bg-black/5 disabled:opacity-40"
              data-testid="catalog-next-page"
            >
              Next →
            </button>
          </div>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">{editing ? "Edit item" : "New item"}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2"><Label>Name *</Label><Input data-testid="product-form-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-none" /></div>
            <div>
              <Label>Type</Label>
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                <SelectTrigger className="rounded-none"><SelectValue /></SelectTrigger>
                <SelectContent>{TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Category</Label><Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="rounded-none" /></div>
            <div><Label>Unit</Label><Input value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} placeholder="Unit, Hour, kg…" className="rounded-none" /></div>
            <div><Label>Price (₹) *</Label><Input type="number" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="rounded-none" /></div>
            <div className="col-span-2"><Label>Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="rounded-none" rows={3} /></div>
            <div className="col-span-2"><Label>Core features (used for social-asset generation)</Label><Textarea value={form.core_features || ""} onChange={(e) => setForm({ ...form, core_features: e.target.value })} rows={2} placeholder="e.g. Portable X-ray, 100mA, DR panel, wireless" className="rounded-none" data-testid="product-form-core-features"/></div>
            <div className="col-span-2"><Label>Tone / Style hint</Label><Input value={form.tone_style || ""} onChange={(e) => setForm({ ...form, tone_style: e.target.value })} placeholder="professional healthcare marketing" className="rounded-none" data-testid="product-form-tone-style"/></div>
            {editing && (
              <div className="col-span-2 text-xs text-[#717680] flex items-center gap-2 border-t border-black/5 pt-3">
                <FileText size={14} /> You can attach a brochure (PDF/image) directly from the catalog card after saving.
              </div>
            )}
          </div>
          <DialogFooter>
            <button onClick={() => setOpen(false)} className="px-4 py-2 border border-black/15 text-sm">Cancel</button>
            <button onClick={save} data-testid="save-product-button" className="px-4 py-2 bg-[#D90429] text-white text-sm font-semibold hover:bg-[#EF233C]">{editing ? "Save" : "Add"}</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Webhook Settings Dialog */}
      <Dialog open={webhookOpen} onOpenChange={setWebhookOpen}>
        <DialogContent className="rounded-none max-w-2xl" data-testid="webhook-settings-dialog">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">Asset Generation Webhook</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 text-sm">
            <div className="border border-[#0D9488]/30 bg-[#0D9488]/5 p-3 text-[12px] leading-relaxed">
              <b>Two modes available:</b>
              <ul className="mt-1.5 space-y-1 list-none">
                <li><b>● IN-HOUSE (default, leave URL empty)</b> — Gemini Nano Banana generates the image directly inside Reyansh CRM using your Emergent LLM Key. Free, no external service, ~15 s per image. Recommended for most users.</li>
                <li><b>● EXTERNAL (paste a URL below)</b> — Reyansh POSTs product details to your hosted endpoint (Vercel/Cloudflare/Lambda running the Replicate/Flux snippet). Your service generates the image and calls back to <code>POST /api/products/&lt;id&gt;/assets</code>.</li>
              </ul>
              <div className="mt-2">In both modes, when the image is ready you approve it and it auto-posts to LinkedIn.</div>
            </div>
            <div>
              <Label>Webhook URL <span className="text-[10px] text-[#717680] font-mono-shelv">(leave empty for IN-HOUSE mode)</span></Label>
              <Input value={webhookCfg.webhook_url || ""} onChange={(e) => setWebhookCfg({ ...webhookCfg, webhook_url: e.target.value })} placeholder="Leave empty for in-house Nano Banana" className="rounded-none font-mono text-xs" data-testid="webhook-url-input"/>
            </div>
            {webhookCfg.api_key && (
              <div>
                <Label>API Key (paste into your Emergent AI as <code>REYANSH_CRM_API_KEY</code>)</Label>
                <div className="flex items-center gap-2">
                  <Input value={webhookCfg.api_key} readOnly className="rounded-none font-mono text-xs bg-black/5" data-testid="webhook-api-key"/>
                  <button onClick={() => { navigator.clipboard.writeText(webhookCfg.api_key); toast.success("Copied"); }} className="px-3 py-2 border border-black/15 text-xs">Copy</button>
                </div>
                <div className="text-[10px] text-[#717680] mt-1 font-mono-shelv">
                  Your generator must send this key back in <code>Authorization: Bearer &lt;key&gt;</code> when POSTing to <code>/api/products/&lt;id&gt;/assets</code>.
                </div>
              </div>
            )}
            <div className="border border-black/10 p-3 text-[11px] font-mono-shelv text-[#4A4D57] leading-relaxed">
              <b>Outbound payload → your URL:</b><br/>
              <code>{`{ "productId": "prod_xxx", "productName": "…", "coreFeatures": "…", "toneStyle": "…" }`}</code><br/>
              <b>Callback → us:</b><br/>
              <code>{`POST /api/products/<id>/assets  Header: Authorization: Bearer <api_key>  Body: { "generated_asset_url": "https://…", "generation_prompt": "…" }`}</code>
            </div>
          </div>
          <DialogFooter>
            <button onClick={testWebhookCfg} disabled={!webhookCfg.has_config || webhookTesting} className="px-4 py-2 border border-black/15 text-sm disabled:opacity-40" data-testid="webhook-test-button">
              {webhookTesting ? "Pinging…" : "Send test ping"}
            </button>
            <button onClick={() => setWebhookOpen(false)} className="px-4 py-2 border border-black/15 text-sm">Close</button>
            <button onClick={saveWebhookCfg} disabled={webhookSaving} data-testid="webhook-save-button" className="px-4 py-2 bg-[#D90429] text-white text-sm font-semibold hover:bg-[#EF233C] disabled:opacity-50">
              {webhookSaving ? "Saving…" : "Save"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
